import { z } from 'zod';
import { EmailDomainSchema } from './auth-settings.types.js';
import { PaginationParamsSchema, paginatedResponseSchema } from './pagination.types.js';
import {
  BODY_LEN,
  CustomSandboxSizeSchema,
  MAX_CUSTOM_SANDBOX_SIZES,
} from '@runloop/reflex-contract';
import { AccountStatusSchema } from './user.types.js';
import { CORE_IDS, type CoreId } from './ids.js';
import { PERMISSION_SLUG_PATTERN } from './permissions.types.js';
import { SessionSharingSettingsSchema } from './session-sharing-settings.types.js';
import { DefaultSessionShareSchema } from './default-session-share.js';

/**
 * Roles bind permissions. Role scope indicates whether a role applies to an
 * entire organization membership (`org`) or to members of a specific team
 * (`team`).
 */
export const RoleScopeSchema = z.enum(['org', 'team']);
export type RoleScope = z.infer<typeof RoleScopeSchema>;

/**
 * A role's id, in either of the two shapes the roles table holds: a
 * generated `rol_…` core id for custom roles, or the `builtin-<scope>-<slug>`
 * sentinel form seeded for built-in roles. Anything else is not a role id —
 * parse at the boundary with {@link RoleIdSchema} rather than passing a
 * bare string through.
 */
export type RoleId = CoreId<'role'> | `builtin-${RoleScope}-${string}`;

/** Boundary check for {@link RoleId}: a custom `rol_…` id or a built-in sentinel id. */
export const RoleIdSchema = z
  .string()
  .regex(
    new RegExp(`^(?:${CORE_IDS.role}_[A-Za-z0-9]{${BODY_LEN}}|builtin-(?:org|team)-[a-z0-9-]+)$`),
    'Expected a role id (rol_… or builtin-<scope>-<slug>)',
  ) as unknown as z.ZodType<RoleId>;

/**
 * Sandbox retention policy bounds. An org keeps an inactive agent's devbox
 * alive for `sandboxRetentionDays` so work can be resumed, after which a
 * background loop shuts the devbox down and posts a `devbox.cleaned_up`
 * event. Defaults to 30 days; configurable per org by an `org:manage`
 * holder within these bounds. Shared so server validation, the cleanup
 * loop, and the web settings form agree on one source of truth.
 *
 * A `null` `sandboxRetentionDays` means retain forever: the cleanup loop
 * never reclaims the org's devboxes. The numeric bounds only constrain
 * finite windows; `null` is always a valid choice.
 */
export const DEFAULT_SANDBOX_RETENTION_DAYS = 30;
export const MIN_SANDBOX_RETENTION_DAYS = 1;
export const MAX_SANDBOX_RETENTION_DAYS = 365;

export const PermissionSlugSchema = z.string().regex(PERMISSION_SLUG_PATTERN, {
  message: 'Permissions are formatted as "resource:action" in lower-case kebab',
});
export type PermissionSlug = z.infer<typeof PermissionSlugSchema>;

/**
 * Plugin release channel an org can opt into. `released` plugins are
 * unconditional and never appear in the opt-in list; everything else
 * requires the matching channel to be ticked on the per-org plugins
 * page.
 */
export const PluginReleaseChannelSchema = z.enum(['preview', 'alpha', 'beta']);
export type PluginReleaseChannel = z.infer<typeof PluginReleaseChannelSchema>;

/**
 * Full maturity tier for an installable plugin — the org-opt-in channels
 * plus `released`, which is unconditional.
 */
export const PluginReleaseStatusSchema = z.enum(['preview', 'alpha', 'beta', 'released']);
export type PluginReleaseStatus = z.infer<typeof PluginReleaseStatusSchema>;

/**
 * Why an organization's members are blocked from it. Computed on the org
 * view at read time:
 *
 * - `manual` — a platform admin toggled the org off (stored
 *   `status: 'disabled'` with no stored reason).
 * - `subscription_expired` — the org has a {@link OrganizationSubscription}
 *   whose time window does not currently contain `now`. The web shell uses
 *   this to render the "trial has ended" screen instead of the generic
 *   disabled screen.
 * - `nonpayment` — billing disabled the org after dunning exhausted
 *   (stored `status: 'disabled'` plus a stored `disabled_reason` of
 *   `nonpayment`, written by the billing plugin through
 *   `OrganizationService.disableForNonpayment`). The web shell renders the
 *   payment-required screen, and billing cure routes stay reachable so the
 *   org can pay its way back in.
 *
 * `null` when the org is accessible. A text enum (not a boolean) so future
 * reasons (`abuse`, …) can be added without a schema change.
 */
export const OrganizationDisabledReasonSchema = z.enum([
  'manual',
  'subscription_expired',
  'nonpayment',
]);
export type OrganizationDisabledReason = z.infer<typeof OrganizationDisabledReasonSchema>;

/**
 * The kind of subscription an organization holds. `trial` is the
 * provisioning default; `paid` is a Stripe-backed subscription maintained
 * by the billing plugin (reflex has no plan tiers to distinguish, so a
 * single paid type suffices — see
 * `docs/internal/plans/org-billing-usage-payments.md`). The org's access is
 * governed purely by the subscription's time window — see
 * {@link OrganizationSubscriptionSchema}; for `paid` the billing plugin
 * rolls the window forward on every renewal webhook.
 */
export const OrganizationSubscriptionTypeSchema = z.enum(['trial', 'paid']);
export type OrganizationSubscriptionType = z.infer<typeof OrganizationSubscriptionTypeSchema>;

/**
 * Time-bounded subscription for an organization, sourced from the
 * `billing_entity_subscriptions` table (one row per subscribed company). An org
 * is accessible only while `now` falls within `[startsAt, endsAt]`; outside
 * that window the application blocks it (no stored status flip, no cron).
 * `null` for orgs with no subscription (standard, unlimited-access orgs).
 */
export const OrganizationSubscriptionSchema = z.object({
  /** What kind of subscription this is (currently always `trial`). */
  type: OrganizationSubscriptionTypeSchema,
  /** Epoch-ms the subscription window opens (org creation, or when an admin opened it). */
  startsAt: z.number(),
  /** Epoch-ms the subscription window closes. Past this instant, access is blocked. */
  endsAt: z.number(),
});
export type OrganizationSubscription = z.infer<typeof OrganizationSubscriptionSchema>;

/**
 * True when `subscription` exists and `nowMs` falls within its half-open
 * `[startsAt, endsAt)` window — start inclusive, end exclusive. The
 * half-open bound is deliberate: access is derived on read from
 * `(window, now)` with no stored status, so "end the subscription now"
 * (see `OrganizationService.endSubscriptionNow`) sets `endsAt = now` and
 * must read as expired at that same instant. An inclusive upper bound
 * would leave `now` inside the window for one more millisecond, so the
 * org would linger as active until the clock ticked. A `null`/`undefined`
 * subscription is NOT active here — callers treat "no subscription" as
 * "standard org, unlimited access" separately. Defaults to the current
 * epoch-ms so call sites can write `isSubscriptionActive(org.subscription)`;
 * pass `nowMs` to pin the comparison in tests. Kept dependency-free so this
 * types package stays importable from every context (server `tsx`, the
 * OpenAPI generator, the web bundle).
 */
export function isSubscriptionActive(
  subscription: OrganizationSubscription | null | undefined,
  nowMs: number = Date.now(),
): boolean {
  return subscription != null && subscription.startsAt <= nowMs && nowMs < subscription.endsAt;
}

/**
 * Input shape for opening (or extending) an org subscription from the
 * admin surfaces. The server materializes the actual window from
 * `now` + `durationDays`. `type` defaults to `trial` (the only type
 * today); `durationDays` defaults to 14 and is bounded to a year.
 */
export const CreateOrganizationSubscriptionSchema = z.object({
  type: OrganizationSubscriptionTypeSchema.default('trial'),
  durationDays: z.number().int().positive().max(365).default(14),
});
export type CreateOrganizationSubscription = z.infer<typeof CreateOrganizationSubscriptionSchema>;

/**
 * Commercial tier a platform admin assigns to an organization. Unlike
 * {@link OrganizationSubscription}, which governs *access* through a time
 * window, the tier is the entitlement record: the features in
 * {@link SUBSCRIPTION_TIER_FEATURES} are available exactly while the org's
 * tier includes them, and runtime paths fail closed the moment it does not.
 *
 * Every org has one; `standard` is the default for orgs created before the
 * field existed and for orgs an operator never classified.
 */
export const OrganizationSubscriptionTierSchema = z.enum(['trial', 'standard', 'enterprise']);
export type OrganizationSubscriptionTier = z.infer<typeof OrganizationSubscriptionTierSchema>;

/** Display labels for a subscription tier, for any surface that names one. */
export const SUBSCRIPTION_TIER_LABELS: Record<OrganizationSubscriptionTier, string> = {
  trial: 'Trial',
  standard: 'Standard',
  enterprise: 'Enterprise',
};

/** The tier an unclassified organization reads as. */
export const DEFAULT_ORGANIZATION_SUBSCRIPTION_TIER: OrganizationSubscriptionTier = 'standard';

/** Every subscription tier, in ascending commercial order, for pickers. */
export const ORGANIZATION_SUBSCRIPTION_TIERS = OrganizationSubscriptionTierSchema.options;

export const OrganizationSchema = z
  .object({
    id: z.string(),
    slug: z.string(),
    name: z.string(),
    avatarUrl: z.string().nullable(),
    isPersonal: z.boolean(),
    createdByUserId: z.string().nullable(),
    createdAt: z.number(),
    /**
     * Optional support email shown to users whose email domain is not
     * claimed by any org. When empty, the rejection message falls back
     * to `support@<email-domain>` derived from the visitor's email.
     */
    supportEmail: z.string().email().nullable(),
    /**
     * Email domains the org has claimed. A user logging in with no
     * non-personal org membership and a matching domain will be auto-
     * joined as `org:member`. Each domain is globally unique across
     * orgs (enforced at the DB layer).
     */
    emailDomains: z.array(EmailDomainSchema),
    /**
     * Per-org "phase 2 setup complete" timestamp. `null` means the org
     * has never been bootstrapped (plugin selection, sandbox key,
     * integrations). The web AuthGate uses this to force a permitted
     * user into the `/:orgSlug/setup` wizard before any other surface
     * mounts. Optional in the wire schema so legacy fixtures without
     * the field still validate.
     */
    bootstrappedAt: z.number().nullable().optional(),
    /**
     * Free-form capability slugs granted to this org by a platform admin.
     * Plugins that declare `requiredCapabilities` are only available to
     * orgs whose capabilities superset the plugin's required set. The
     * host does not validate slug values, it only checks set membership
     * against `PluginDefinition.orgInstall.requiredCapabilities`.
     *
     * Defaults to `[]` at parse time so consumers can iterate without
     * `?? []` boilerplate. Legacy fixtures and test mocks built before
     * this field existed continue to round-trip — the default fills in
     * for the missing field.
     */
    capabilities: z.array(z.string()).default([]),
    /**
     * The org's subscription tier — the first-class entitlement record. See
     * {@link OrganizationSubscriptionTierSchema}. Optional in the wire schema
     * so legacy fixtures without the field still validate; the server always
     * populates it (defaulting to
     * {@link DEFAULT_ORGANIZATION_SUBSCRIPTION_TIER}). When the org belongs
     * to a billing entity this column is dormant — the billing entity's tier governs, and
     * only the platform-admin `AdminOrganizationBillingEntity` read reports that.
     * Commercial ownership is deliberately absent from this resource: an
     * billing entity is an internal concept, so it never reaches an org's members.
     */
    subscriptionTier: OrganizationSubscriptionTierSchema.optional(),
    /**
     * Pre-release plugin channels the org has opted into. Plugins whose
     * `orgInstall.releaseStatus` is not `released` are only available when
     * the matching channel appears here. `released` is implicit and never
     * appears. Toggled by org managers (`org:manage`) on the per-org
     * plugins page.
     *
     * Defaults to `[]` at parse time so consumers can iterate without
     * `?? []` boilerplate and so legacy fixtures (built before this field
     * existed) continue to round-trip.
     */
    enabledReleaseChannels: z.array(PluginReleaseChannelSchema).default([]),
    /**
     * Manual lifecycle status. See {@link AccountStatusSchema}. When
     * `disabled`, a platform admin toggled the org off and the server
     * rejects every request whose active org resolves to this org with
     * HTTP 403 `organization_disabled`. Time-limited access is governed
     * by {@link OrganizationSubscription}, not this field. Optional in the
     * wire schema so legacy fixtures without the field still validate.
     */
    status: AccountStatusSchema.optional(),
    /**
     * Computed reason the org's members are blocked, or `null` when the
     * org is accessible. `'manual'` when `status === 'disabled'`;
     * `'subscription_expired'` when a subscription window no longer
     * contains `now` (drives the web "trial has ended" screen). Derived
     * server-side at read time from `status` + `subscription`, never
     * stored. Optional in the wire schema so legacy fixtures without the
     * field still validate.
     */
    disabledReason: OrganizationDisabledReasonSchema.nullable().optional(),
    /**
     * Time-bounded subscription for orgs that hold one (e.g. a trial).
     * `null` for standard orgs with unlimited access. Sourced from
     * `billing_entity_subscriptions`. Access is granted only while `now`
     * falls within the window. Optional in the wire schema so legacy
     * fixtures without the field still validate.
     */
    subscription: OrganizationSubscriptionSchema.nullable().optional(),
    /**
     * How many days the org keeps an inactive agent's devbox alive before
     * the host cleans it up. Completing an agent no longer kills its
     * devbox; instead the box lingers for this window so work can be
     * resumed, then a background loop shuts it down and posts a
     * `devbox.cleaned_up` event. Defaults to
     * {@link DEFAULT_SANDBOX_RETENTION_DAYS}. `null` means retain forever,
     * so the cleanup loop never reclaims this org's devboxes. Optional in
     * the wire schema so legacy fixtures without the field still validate;
     * the server always populates it (a number or `null`).
     */
    sandboxRetentionDays: z
      .number()
      .int()
      .min(MIN_SANDBOX_RETENTION_DAYS)
      .max(MAX_SANDBOX_RETENTION_DAYS)
      .nullable()
      .optional(),
    /**
     * The org's default devbox size, applied when a launch leaves the size
     * unset and shown as the launcher's "(Default)" option. Either a named
     * `ResourceSize` enum value or the name of one of the org's
     * `customSandboxSizes`. `null`/absent means the platform default
     * (`DEFAULT_SANDBOX_RESOURCE_SIZE`) applies. Resolve it with
     * `resolveOrgDefaultSandboxSize`.
     */
    defaultSandboxSize: z.string().nullable().optional(),
    /**
     * Org-defined custom devbox sizes offered in the launcher's size picker
     * alongside the named tiers. Names are unique within the org
     * (case-insensitive) and may not shadow a named `ResourceSize` value.
     * Optional in the wire schema so legacy fixtures without the field still
     * validate; the server always populates it (possibly `[]`).
     */
    customSandboxSizes: z.array(CustomSandboxSizeSchema).optional(),
    /**
     * Whether this org's devboxes route package installs (pip/uv, Node
     * toolchain downloads) through the Runloop environment's artifact
     * mirror. On by default; the agent runner stamps the effective value
     * onto every launch's `SandboxOptions.artifactMirrors` — there is no
     * per-launch toggle. Optional in the wire schema so legacy fixtures
     * without the field still validate; the server always populates it.
     */
    sandboxArtifactMirrors: z.boolean().optional(),
    /**
     * Whether members of this org may share a session read-only ("Can
     * view"). On by default. See `SessionSharingPolicy` in
     * `session-sharing.ts` — with this and
     * {@link allowSessionShareEdit} both off, sessions are not shareable
     * and the share affordance is hidden. Optional in the wire schema so
     * legacy fixtures without the field still validate; the server always
     * populates it.
     */
    allowSessionShareView: z.boolean().optional(),
    /**
     * Whether members of this org may share a session with write access
     * ("Can edit"). On by default. Turning it off is a separation-of-duties
     * control: the server refuses to confer write on someone else's session
     * for every caller, org admins included.
     */
    allowSessionShareEdit: z.boolean().optional(),
    /**
     * Org choices for the sharing settings plugins register
     * (`SessionSharingSetting` in `session-sharing-settings.ts`), keyed by
     * `<plugin>:<name>`. A key with no entry reads as that setting's
     * declared default, and every value is subordinate to the two levels
     * above — read one through `sessionSharingSettingEnabled`, never off
     * this map directly.
     */
    sharingSettings: SessionSharingSettingsSchema.optional(),
    /**
     * How a session in this org starts out: `private` to whoever launched
     * it (the default), or already shared with every member at `view` or
     * `edit`. Applied once, when the session is created, by opening an
     * ordinary org-subject grant — so changing it never reaches back and
     * re-opens or strips sessions that already exist. Subordinate to the
     * two levels above: read it through `effectiveDefaultSessionShare` in
     * `session-sharing.ts`, never off this field directly.
     */
    defaultSessionShare: DefaultSessionShareSchema.optional(),
  })
  // Tagged so the OpenAPI generator emits one reusable `Organization`
  // component referenced by the `organizations` routes. See
  // docs/internal/architecture/api-contract.md.
  .meta({ id: 'Organization' });
export type Organization = z.infer<typeof OrganizationSchema>;

/**
 * Capability slug that entitles an org to devbox snapshots. Gates both the
 * server snapshot routes and the web affordances (the agent "Save snapshot"
 * action and the Blueprints page snapshot list). Granted by a platform admin
 * through `PATCH /admin/organizations/:id/capabilities`, like any other
 * capability slug. Lives here so server and web share one literal.
 */
export const DEVBOX_SNAPSHOTS_CAPABILITY = 'devbox-snapshots';

/** True when the org's capability set entitles it to devbox snapshots. */
export function hasDevboxSnapshotsCapability(capabilities: readonly string[] | undefined): boolean {
  return capabilities?.includes(DEVBOX_SNAPSHOTS_CAPABILITY) ?? false;
}

/**
 * Capability slug that lets the host inject events that happen outside the
 * chat (pull-request updates, for now) into a running Codex agent's
 * conversation. Granted by a platform admin through
 * `PATCH /admin/organizations/:id/capabilities`, like any other capability
 * slug. Lives here so server and web share one literal.
 */
export const CODEX_INJECTION_CAPABILITY = 'codex-injection';

/** True when the org's capability set allows Codex injection. */
export function hasCodexInjectionCapability(capabilities: readonly string[] | undefined): boolean {
  return capabilities?.includes(CODEX_INJECTION_CAPABILITY) ?? false;
}

/**
 * Feature entitled by subscription tier rather than by a capability slug. A
 * tier feature is never written into `capabilities`: the org's current tier
 * is the single authoritative entitlement record, so changing the tier
 * immediately changes what the runtime allows. Identity paths fail closed on
 * downgrade while the org's configuration stays retained.
 */
export const ENTERPRISE_IDENTITY_FEATURE = 'enterprise-identity';
export type SubscriptionTierFeature = typeof ENTERPRISE_IDENTITY_FEATURE;

/**
 * The wire `error` code every enterprise-identity route answers with when the
 * governing tier does not include {@link ENTERPRISE_IDENTITY_FEATURE}.
 *
 * It lives here, beside the feature it refuses, because it is a contract and
 * not a message: clients branch on it to tell a refusal ("no save on this
 * plan will ever be accepted") from an outage ("try again"), and that branch
 * is an exact string match. Spelling it separately on each side would let a
 * rename revert every one of those surfaces to a red error box with no test
 * failing.
 */
export const ENTERPRISE_IDENTITY_NOT_ENTITLED_CODE = 'enterprise_identity_not_entitled';

/**
 * The features each tier includes. The single place that says what a tier
 * entitles — server SQL predicates, service checks, and admin UI copy all
 * derive from this map.
 */
export const SUBSCRIPTION_TIER_FEATURES: Record<
  OrganizationSubscriptionTier,
  readonly SubscriptionTierFeature[]
> = {
  trial: [],
  standard: [],
  enterprise: [ENTERPRISE_IDENTITY_FEATURE],
};

/** Tiers whose feature set includes `feature`, for SQL membership predicates. */
export function subscriptionTiersWithFeature(
  feature: SubscriptionTierFeature,
): OrganizationSubscriptionTier[] {
  return ORGANIZATION_SUBSCRIPTION_TIERS.filter((tier) =>
    SUBSCRIPTION_TIER_FEATURES[tier].includes(feature),
  );
}

/** True when `tier` includes `feature`. An absent tier reads as the default tier. */
export function subscriptionTierHasFeature(
  tier: OrganizationSubscriptionTier | undefined,
  feature: SubscriptionTierFeature,
): boolean {
  return SUBSCRIPTION_TIER_FEATURES[tier ?? DEFAULT_ORGANIZATION_SUBSCRIPTION_TIER].includes(
    feature,
  );
}

/** True when the org's tier entitles it to enterprise identity. */
export function hasEnterpriseIdentityTier(tier: OrganizationSubscriptionTier | undefined): boolean {
  return subscriptionTierHasFeature(tier, ENTERPRISE_IDENTITY_FEATURE);
}

/**
 * The tier that actually governs an organization: its billing entity's tier when it
 * belongs to one, its own otherwise. A billing entity is the commercial owner (see
 * `billing-entity.types.ts`), so attaching an org hands tier governance to the
 * billing entity and the org's own column goes dormant until it is detached.
 * Shared so the server's SQL predicates and the admin UI resolve the same
 * way.
 */
export function effectiveSubscriptionTier(
  billingEntityTier: OrganizationSubscriptionTier | null | undefined,
  organizationTier: OrganizationSubscriptionTier | undefined,
): OrganizationSubscriptionTier {
  return billingEntityTier ?? organizationTier ?? DEFAULT_ORGANIZATION_SUBSCRIPTION_TIER;
}

/** Platform-admin mutation that assigns an organization's subscription tier. */
export const SetOrganizationSubscriptionTierRequestSchema = z.object({
  subscriptionTier: OrganizationSubscriptionTierSchema,
});
export type SetOrganizationSubscriptionTierRequest = z.infer<
  typeof SetOrganizationSubscriptionTierRequestSchema
>;

/** Atomic platform-admin mutation for one capability without replacing the full set. */
export const SetOrganizationCapabilityRequestSchema = z.object({
  capability: z.string().trim().min(1),
  enabled: z.boolean(),
});
export type SetOrganizationCapabilityRequest = z.infer<
  typeof SetOrganizationCapabilityRequestSchema
>;

/**
 * Query parameters accepted by `GET /admin/organizations`. Extends the
 * standard {@link PaginationParamsSchema} with an optional free-text
 * search applied to `name`, `slug`, and `id` (case-insensitive). The
 * server trims whitespace and treats an empty `q` as "no filter".
 */
export const AdminListOrganizationsQuerySchema = PaginationParamsSchema.extend({
  q: z.string().trim().optional(),
});
export type AdminListOrganizationsQuery = z.infer<typeof AdminListOrganizationsQuerySchema>;

export const AdminListOrganizationsResponseSchema = paginatedResponseSchema(OrganizationSchema);
export type AdminListOrganizationsResponse = z.infer<typeof AdminListOrganizationsResponseSchema>;

export const OrganizationEmailDomainSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  domain: EmailDomainSchema,
  createdAt: z.number(),
  createdByUserId: z.string().nullable(),
});
export type OrganizationEmailDomain = z.infer<typeof OrganizationEmailDomainSchema>;

export const AddOrganizationEmailDomainRequestSchema = z.object({
  domain: EmailDomainSchema,
});
export type AddOrganizationEmailDomainRequest = z.infer<
  typeof AddOrganizationEmailDomainRequestSchema
>;

export const RoleSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  scope: RoleScopeSchema,
  isBuiltin: z.boolean(),
  permissions: z.array(PermissionSlugSchema),
});
export type Role = z.infer<typeof RoleSchema>;

export const TeamSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /**
   * Role id auto-assigned to a new team member. `null` only on legacy
   * teams created before the per-member-role migration. Optional so
   * fixtures that pre-date the field still round-trip through the
   * schema and so the inferred TS type allows constructing legacy-
   * shaped mocks without specifying it.
   */
  defaultRoleId: z.string().nullable().optional(),
  createdAt: z.number(),
});
export type Team = z.infer<typeof TeamSchema>;

/** A team plus the aggregate counts the org team-management surfaces render. */
export const TeamViewSchema = TeamSchema.extend({
  memberCount: z.number(),
});
export type TeamView = z.infer<typeof TeamViewSchema>;

/** `{ teams }` envelope for `GET /organizations/:id/teams`. */
export const ListOrganizationTeamsResponseSchema = z.object({
  teams: z.array(TeamViewSchema),
});
export type ListOrganizationTeamsResponse = z.infer<typeof ListOrganizationTeamsResponseSchema>;

/**
 * A team the current user belongs to, plus the slugs their role on that
 * team carries. Those slugs authorize actions on this team only — the
 * server evaluates them with `canInTeam`, never as org-wide authority — so
 * they are reported per team rather than folded into the membership's
 * org-wide `permissions`.
 */
export const MembershipTeamSchema = TeamSchema.extend({
  permissions: z.array(PermissionSlugSchema),
});
export type MembershipTeam = z.infer<typeof MembershipTeamSchema>;

/**
 * `permissions` are the org-wide slugs, from the caller's org-membership
 * roles; `teams` are the teams the current user belongs to within this
 * organization, each carrying its own slugs. Used by the web UI's
 * `OwnerSelect` picker so users can scope a freshly-created resource to a
 * specific team they belong to, and by the team pages to gate controls a
 * team role allows. The list is empty when the user is not on any team in
 * this org. The server enforces the same membership check via
 * `assertCallerCanOwn`, so missing teams here are a UX gap, not a security
 * one.
 */
export const OrganizationMembershipSchema = z.object({
  memberId: z.string(),
  organization: OrganizationSchema,
  roles: z.array(RoleSchema),
  permissions: z.array(PermissionSlugSchema),
  teams: z.array(MembershipTeamSchema),
});
export type OrganizationMembership = z.infer<typeof OrganizationMembershipSchema>;

/**
 * `{ organizations }` envelope for `GET /organizations` — the caller's own
 * memberships. External SDK consumers use it to discover which orgs an API
 * key can act in (e.g. to offer an org picker before launching agents).
 */
export const ListOrganizationsResponseSchema = z
  .object({
    organizations: z.array(OrganizationMembershipSchema),
  })
  .meta({ id: 'ListOrganizationsResponse' });
export type ListOrganizationsResponse = z.infer<typeof ListOrganizationsResponseSchema>;

/** `{ organizations }` envelope for `GET /admin/users/:userId/organizations`. */
export const AdminUserOrganizationsResponseSchema = z.object({
  organizations: z.array(OrganizationMembershipSchema),
});
export type AdminUserOrganizationsResponse = z.infer<typeof AdminUserOrganizationsResponseSchema>;

/**
 * Self-described onboarding profile collected at create time on both the
 * self-service wizard and the admin create-org dialog. Reflex derives the
 * org's starting plugin set from this one broad question instead of asking
 * users to pick plugins (see the server's `computeBootstrapSetForProfile`,
 * which keeps each default plugin whose advertised `audiences` match the
 * profile):
 *
 *   - `engineering` — teams that build and ship software; get the full
 *     default plugin set (coding agents, sandbox tooling, code review, GitHub).
 *   - `general` — everything else; get the neutral defaults (chat, flows,
 *     costs, personas, …) without the developer tooling.
 *
 * The profile is persisted in the `organization_onboarding` table (not on the
 * `organizations` row) so a resumed onboarding flow can tell which variant to
 * show even when the user abandoned it after the first screen.
 */
export const OrgOnboardingProfileSchema = z.enum(['engineering', 'general']);
export type OrgOnboardingProfile = z.infer<typeof OrgOnboardingProfileSchema>;

export const CreateOrganizationRequestSchema = z.object({
  companyName: z.string().min(1).max(200).optional(),
  companySlug: z.string().min(1).max(100).optional(),
  name: z.string().min(1).max(100),
  slug: z.string().min(1).max(60).optional(),
  avatarUrl: z.string().url().optional(),
  /**
   * Optional support email shown in the OAuth rejection message when
   * a visitor's domain isn't claimed by any org. When omitted, the
   * rejection message falls back to `support@<email-domain>`.
   */
  supportEmail: z.string().email().optional(),
  /**
   * Email domains the new org claims. Each domain must be globally
   * unique across orgs; collisions return a structured 409. Pass an
   * empty array to skip claims at create time.
   */
  emailDomains: z.array(EmailDomainSchema).optional(),
  /**
   * Plugin names to install for the new organization. When omitted, the
   * server bootstraps from each plugin's `defaultInstalled` flag. When
   * provided, the array is treated as the exhaustive set: any installable
   * plugin not in the list gets an `uninstalled` row. Unknown names,
   * `installable: false` plugins, and selections with missing dependency
   * closures are rejected with `400` before the org row is inserted.
   */
  pluginNames: z.array(z.string()).optional(),
  /**
   * Self-described onboarding profile. When provided (and `pluginNames` is
   * omitted), the server derives the starting plugin selection from it — the
   * single source of truth for the eng/general split, so the client never has
   * to compute a plugin list — and records it in the `organization_onboarding`
   * table. Explicit `pluginNames` still wins when both are sent. See
   * {@link OrgOnboardingProfileSchema}.
   */
  orgOnboardingProfile: OrgOnboardingProfileSchema.optional(),
});
export type CreateOrganizationRequest = z.infer<typeof CreateOrganizationRequestSchema>;

/**
 * Owner specification shared by the admin org-create surfaces. The owner is
 * either an existing user (`ownerUserId`) or a "pending" user pre-provisioned
 * by email (`pendingOwner`) so an org can be created for someone who has never
 * signed in. Exactly one must be provided; the server mints the pending user
 * atomically with the org.
 */
export const AdminOrgPendingOwnerSchema = z.object({
  email: z.string().email(),
  name: z.string().min(1).max(100).optional(),
});
export type AdminOrgPendingOwner = z.infer<typeof AdminOrgPendingOwnerSchema>;

/**
 * Refinement enforcing "exactly one owner source". Shared by the plain and
 * from-template admin org-create schemas so both reject a request that names
 * both an existing owner and a pending one (or neither).
 */
export function refineExactlyOneOwner<T extends { ownerUserId?: string; pendingOwner?: unknown }>(
  values: T,
  ctx: z.RefinementCtx,
): void {
  const provided = (values.ownerUserId ? 1 : 0) + (values.pendingOwner ? 1 : 0);
  if (provided !== 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['ownerUserId'],
      message: 'Provide exactly one of ownerUserId or pendingOwner',
    });
  }
}

/**
 * Super-admin-only "create organization on behalf of someone else" request.
 * Unlike `CreateOrganizationRequestSchema`, the owner is specified explicitly
 * rather than defaulting to the requester — either an existing `ownerUserId`
 * or a pre-provisioned `pendingOwner` (see {@link AdminOrgPendingOwnerSchema}).
 */
const AdminCreateOrganizationFieldsSchema = z.object({
  companyName: z.string().min(1).max(200).optional(),
  companySlug: z.string().min(1).max(100).optional(),
  name: z.string().min(1).max(100),
  slug: z.string().min(1).max(60).optional(),
  avatarUrl: z.string().url().optional(),
  ownerUserId: z.string().min(1).optional(),
  /** Pre-provision a pending owner by email. See {@link AdminOrgPendingOwnerSchema}. */
  pendingOwner: AdminOrgPendingOwnerSchema.optional(),
  /** See {@link CreateOrganizationRequestSchema.supportEmail}. */
  supportEmail: z.string().email().optional(),
  /** See {@link CreateOrganizationRequestSchema.emailDomains}. */
  emailDomains: z.array(EmailDomainSchema).optional(),
  /**
   * Plugin names to install for the new organization. See
   * {@link CreateOrganizationRequestSchema.pluginNames}.
   */
  pluginNames: z.array(z.string()).optional(),
  /** See {@link CreateOrganizationRequestSchema.orgOnboardingProfile}. */
  orgOnboardingProfile: OrgOnboardingProfileSchema.optional(),
  /**
   * Free-form capability slugs to grant the new org at create time.
   * Persisted on the org row BEFORE plugin bootstrap validation runs so
   * capability-gated plugins selected in the same request pass the
   * preflight. Equivalent to a follow-up
   * `PATCH /admin/organizations/:id/capabilities`, but atomic with create.
   */
  capabilities: z.array(z.string()).optional(),
  /**
   * Optional time-limited subscription to attach to the new org. When
   * present, the server inserts a `billing_entity_subscriptions` row with
   * `startsAt = now`, `endsAt = now + durationDays`. Omit it to create a
   * standard org with unlimited access.
   */
  subscription: CreateOrganizationSubscriptionSchema.optional(),
});

export const AdminCreateOrganizationRequestSchema =
  AdminCreateOrganizationFieldsSchema.superRefine(refineExactlyOneOwner);
export type AdminCreateOrganizationRequest = z.infer<typeof AdminCreateOrganizationRequestSchema>;

/**
 * Admin-console empty-org creation that first provisions a Runloop account.
 * The idempotency key is sent only to the server; it becomes the durable
 * upstream Runloop operation key and never exposes the service credential.
 */
export const AdminCreateProvisionedOrganizationRequestSchema =
  AdminCreateOrganizationFieldsSchema.extend({
    idempotencyKey: z.string().min(1).max(255),
  }).superRefine(refineExactlyOneOwner);
export type AdminCreateProvisionedOrganizationRequest = z.infer<
  typeof AdminCreateProvisionedOrganizationRequestSchema
>;

export const UpdateOrganizationRequestSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  slug: z.string().min(1).max(60).optional(),
  avatarUrl: z.string().url().nullable().optional(),
  supportEmail: z.string().email().nullable().optional(),
  /**
   * Replace the org's full capability list. Platform-admin only at the
   * route layer (see `organization.routes.ts`); the schema itself
   * accepts the field unconditionally so non-admin clients sending
   * it get a 403, not a 400.
   */
  capabilities: z.array(z.string()).optional(),
  /**
   * Replace the org's full set of opted-in pre-release plugin channels.
   * Gated by the `org:manage` permission at the route layer. `released`
   * is implicit and never appears here.
   */
  enabledReleaseChannels: z.array(PluginReleaseChannelSchema).optional(),
  /**
   * Update the org's sandbox retention window (days). Gated by the
   * `org:manage` permission at the route layer. A finite window is bounded
   * by {@link MIN_SANDBOX_RETENTION_DAYS}/{@link MAX_SANDBOX_RETENTION_DAYS};
   * out-of-range values are rejected with a 400. Pass `null` to retain
   * devboxes forever (the cleanup loop never reclaims them); omit the field
   * to leave the current window unchanged.
   */
  sandboxRetentionDays: z
    .number()
    .int()
    .min(MIN_SANDBOX_RETENTION_DAYS)
    .max(MAX_SANDBOX_RETENTION_DAYS)
    .nullable()
    .optional(),
  /**
   * Set the org's default devbox size. Gated by the `org:manage` permission
   * at the route layer. Must be a named `ResourceSize` enum value or the
   * name of one of the org's custom sizes (validated server-side against
   * the effective `customSandboxSizes` list). Pass `null` to fall back to
   * the platform default; omit the field to leave it unchanged.
   */
  defaultSandboxSize: z.string().trim().min(1).nullable().optional(),
  /**
   * Replace the org's full list of custom devbox sizes. Gated by the
   * `org:manage` permission at the route layer. Names must be unique
   * within the org (case-insensitive) and may not shadow a named
   * `ResourceSize` value.
   */
  customSandboxSizes: z.array(CustomSandboxSizeSchema).max(MAX_CUSTOM_SANDBOX_SIZES).optional(),
  /**
   * Opt the org's devboxes in or out of Runloop's package-registry artifact
   * mirrors. Gated by the `org:manage` permission at the route layer. Omit
   * the field to leave the current setting unchanged.
   */
  sandboxArtifactMirrors: z.boolean().optional(),
  /**
   * Allow or forbid read-only session shares org-wide. Gated by the
   * `org:manage` permission at the route layer. Omit to leave unchanged.
   */
  allowSessionShareView: z.boolean().optional(),
  /**
   * Allow or forbid write ("Can edit") session shares org-wide. Gated by
   * the `org:manage` permission at the route layer. Omit to leave
   * unchanged. Existing grants are not revoked by turning it off; no new
   * write share can be opened while it is off.
   */
  allowSessionShareEdit: z.boolean().optional(),
  /**
   * Choices for plugin-registered sharing settings, keyed by
   * `<plugin>:<name>`. Merged over what the org has stored, so a body may
   * carry only the keys it changes; a key no plugin declares is rejected.
   * Gated by `org:manage` at the route layer. Turning one off stops the
   * next share it would have opened; grants already opened stay open, same
   * as the two levels above.
   */
  sharingSettings: SessionSharingSettingsSchema.optional(),
  /**
   * How new sessions start out org-wide. Gated by `org:manage` at the
   * route layer. Omit to leave unchanged. Applies only to sessions created
   * after the change: sessions that already exist keep the access they
   * were created with, the same way narrowing the two levels above leaves
   * open shares alone.
   */
  defaultSessionShare: DefaultSessionShareSchema.optional(),
});
export type UpdateOrganizationRequest = z.infer<typeof UpdateOrganizationRequestSchema>;

export const CreateTeamRequestSchema = z.object({
  name: z.string().min(1).max(100),
  slug: z.string().min(1).max(60).optional(),
  description: z.string().max(500).nullable().optional(),
  /** Initial default role for new team members. Defaults to built-in `team-member`. */
  defaultRoleId: z.string().min(1).optional(),
});
export type CreateTeamRequest = z.infer<typeof CreateTeamRequestSchema>;

export const UpdateTeamRequestSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  slug: z.string().min(1).max(60).optional(),
  description: z.string().max(500).nullable().optional(),
  defaultRoleId: z.string().min(1).optional(),
});
export type UpdateTeamRequest = z.infer<typeof UpdateTeamRequestSchema>;

export const SetTeamMemberRoleRequestSchema = z.object({
  roleId: z.string().min(1),
});
export type SetTeamMemberRoleRequest = z.infer<typeof SetTeamMemberRoleRequestSchema>;

export const AddOrganizationMemberRequestSchema = z.object({
  userId: z.string().min(1),
});
export type AddOrganizationMemberRequest = z.infer<typeof AddOrganizationMemberRequestSchema>;

/**
 * Lifecycle status of an organization invite. Pending invites are
 * redeemable; the other four are terminal and exposed for audit and
 * the public landing route. `declined` is set when the invited user
 * explicitly rejects the invite — a one-time action that, like
 * `consumed`/`revoked`, frees the pending slot for a fresh re-invite.
 */
export const OrganizationInviteStatusSchema = z.enum([
  'pending',
  'consumed',
  'revoked',
  'expired',
  'declined',
]);
export type OrganizationInviteStatus = z.infer<typeof OrganizationInviteStatusSchema>;

/**
 * Filter for the invite listing: the statuses above plus two composites —
 * `'active'` (pending + expired, what the invite panel asks for so a
 * lapsed invite stays resendable) and `'all'`. The server treats an
 * unrecognised value as the default `'pending'` rather than rejecting it.
 */
export const OrganizationInviteListFilterSchema = z.enum([
  'pending',
  'active',
  'consumed',
  'revoked',
  'declined',
  'all',
]);
export type OrganizationInviteListFilter = z.infer<typeof OrganizationInviteListFilterSchema>;

/**
 * Server-rendered view of an organization invite. The plaintext token
 * is intentionally absent — it is only returned as a sibling field on
 * the create or resend response, so admins can share the current redeem
 * URL exactly once.
 */
export const OrganizationInviteSchema = z
  .object({
    id: z.string(),
    organizationId: z.string(),
    email: z.string().email(),
    /** The original inviter. Immutable — token rotations never change it. */
    invitedByUserId: z.string(),
    /** Last token rotation (resend or re-invite); null when never rotated. */
    reissuedAt: z.number().nullable(),
    reissuedByUserId: z.string().nullable(),
    createdAt: z.number(),
    expiresAt: z.number(),
    consumedAt: z.number().nullable(),
    consumedByUserId: z.string().nullable(),
    revokedAt: z.number().nullable(),
    revokedByUserId: z.string().nullable(),
    declinedAt: z.number().nullable(),
    declinedByUserId: z.string().nullable(),
    status: OrganizationInviteStatusSchema,
    tokenLast4: z.string(),
    /**
     * Org-scoped role the invitee receives on top of the built-in
     * `org/member` every new member gets. `null` means member-only.
     *
     * Held as an intent, not a grant: the role is applied when the invite is
     * redeemed, so a role deleted in the meantime simply does not land and
     * the invitee joins as a plain member.
     */
    roleId: z.string().nullable(),
  })
  .meta({ id: 'OrganizationInvite' });
export type OrganizationInvite = z.infer<typeof OrganizationInviteSchema>;

export const CreateOrganizationInviteRequestSchema = z.object({
  email: z.string().email(),
  /**
   * Org-scoped role to grant on redemption, on top of `org/member`. Omit for
   * a plain member. Choosing one is a privileged act — the route requires
   * `roles:manage` on top of `members:invite`, because otherwise anyone who
   * can invite could mint an admin.
   */
  roleId: z.string().optional(),
});
export type CreateOrganizationInviteRequest = z.infer<typeof CreateOrganizationInviteRequestSchema>;

export const CreateOrganizationInviteResponseSchema = z
  .object({
    invite: OrganizationInviteSchema,
    /** Plaintext share URL; only returned once, on this create response. */
    redeemUrl: z.string().url(),
    /**
     * Whether the invite email was handed to the email provider. `false` when
     * email is not configured for the deployment (the self-hosted / VPC
     * default) or when delivery failed, in which case the admin has to share
     * {@link redeemUrl} themselves.
     */
    emailSent: z.boolean(),
    /**
     * True when this call rotated an existing live invite's token, killing
     * any previously shared link. Always `true` on a resend response.
     */
    reissued: z.boolean(),
  })
  .meta({ id: 'CreateOrganizationInviteResponse' });
export type CreateOrganizationInviteResponse = z.infer<
  typeof CreateOrganizationInviteResponseSchema
>;

export const ListOrganizationInvitesResponseSchema = z.object({
  invites: z.array(OrganizationInviteSchema),
});
export type ListOrganizationInvitesResponse = z.infer<typeof ListOrganizationInvitesResponseSchema>;

/**
 * Public landing-page metadata for an invite token. Returned by the
 * unauthenticated `GET /invites/:token` endpoint so the SPA can render
 * "You've been invited to <org>" without leaking the invite list to
 * the world. Status `not_found` is the only branch where the org
 * fields are absent.
 */
export const InviteLandingResponseSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('not_found'),
  }),
  z.object({
    status: z.enum(['pending', 'consumed', 'revoked', 'expired', 'declined']),
    // The invite's id. Safe to expose on this public endpoint — the
    // secret is the share token, not the id; the authed, email-gated
    // `POST /invites/:inviteId/decline` is what actually mutates state.
    // The landing page uses it to let a signed-in invitee decline.
    inviteId: z.string(),
    organizationId: z.string(),
    organizationName: z.string(),
    organizationAvatarUrl: z.string().nullable(),
    invitedEmail: z.string().email(),
  }),
]);
export type InviteLandingResponse = z.infer<typeof InviteLandingResponseSchema>;

/**
 * Response from `POST /invites/accept-pending` (authed). Lists the
 * orgs the caller just joined by consuming pending invites for their
 * own email.
 */
export const AcceptPendingInvitesResponseSchema = z.object({
  joined: z.array(
    z.object({
      organizationId: z.string(),
      organizationName: z.string(),
      organizationSlug: z.string(),
    }),
  ),
});
export type AcceptPendingInvitesResponse = z.infer<typeof AcceptPendingInvitesResponseSchema>;

/** Response from accepting one explicitly selected organization invite. */
export const AcceptInviteResponseSchema = z
  .object({
    joined: z.object({
      organizationId: z.string(),
      organizationName: z.string(),
      organizationSlug: z.string(),
    }),
  })
  .meta({ id: 'AcceptInviteResponse' });
export type AcceptInviteResponse = z.infer<typeof AcceptInviteResponseSchema>;

/**
 * Display summary of a single pending invite addressed to the signed-in
 * user's email. Returned by `GET /invites/pending` so the org-setup
 * welcome screen can surface "you've been invited" prompts without the
 * user pasting a share link. Carries only org display metadata — never
 * the redemption token (acceptance is by authenticated email, not token).
 */
export const PendingInviteSummarySchema = z.object({
  inviteId: z.string(),
  organizationId: z.string(),
  organizationName: z.string(),
  organizationSlug: z.string(),
  organizationAvatarUrl: z.string().nullable(),
  expiresAt: z.number(),
});
export type PendingInviteSummary = z.infer<typeof PendingInviteSummarySchema>;

/**
 * Response from `GET /invites/pending` (authed). Lists every pending
 * invite for the caller's own email so they can accept it from the
 * org-setup screen after signing in with the invited address.
 */
export const ListPendingInvitesResponseSchema = z.object({
  invites: z.array(PendingInviteSummarySchema),
});
export type ListPendingInvitesResponse = z.infer<typeof ListPendingInvitesResponseSchema>;

export const AssignRoleRequestSchema = z.object({
  userId: z.string().min(1),
  roleId: z.string().min(1),
});
export type AssignRoleRequest = z.infer<typeof AssignRoleRequestSchema>;

export const UpdateRoleRequestSchema = z
  .object({
    name: z.string().min(1).max(100).optional(),
    description: z.string().max(500).nullable().optional(),
    permissions: z.array(PermissionSlugSchema).optional(),
  })
  .refine(
    (data) =>
      data.name !== undefined || data.description !== undefined || data.permissions !== undefined,
    { message: 'At least one of name, description, or permissions is required' },
  );
export type UpdateRoleRequest = z.infer<typeof UpdateRoleRequestSchema>;

export const AuthMeResponseSchema = z.object({
  user: z.object({
    id: z.string(),
    email: z.string(),
    login: z.string(),
    name: z.string().nullable(),
    avatarUrl: z.string().nullable(),
    isSuperAdmin: z.boolean().optional(),
  }),
  memberships: z.array(OrganizationMembershipSchema),
  activeOrganizationId: z.string().nullable(),
});
export type AuthMeResponse = z.infer<typeof AuthMeResponseSchema>;
